import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import type { FormFieldDef } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { useCurrentUser } from '../../hooks/useAuth';
import { Hero } from '../../components/ui/Hero';
import { EmptyState } from '../../components/ui/EmptyState';
import { Card, CardSection } from '../../components/ui/Card';
import { SkeletonList } from '../../components/ui/Skeleton';
import { Button } from '../../components/ui/Button';
import { LinkifiedText } from '../../components/ui/LinkifiedText';
import { CheckTaskRow } from '../../components/onboarding/CheckTaskRow';
import { FileUploadField, type PendingFile } from '../../components/onboarding/FileUploadField';
import { statusPill, type OnboardingTask } from '../../components/onboarding/OnboardingTasks';

interface MeView {
  tasks: OnboardingTask[];
}

/**
 * Dedicated, focused page for completing one to-do, upload, or screening
 * check. Reached from /me, mirroring the form (/me/forms/:taskId) and
 * document (/me/sign/:taskId) flows, so the list stays compact and the
 * action gets a full screen (a DBS check's application instructions are
 * far too long to live inline).
 */
export function TaskComplete() {
  const { taskId } = useParams<{ taskId: string }>();
  const { data: user, isLoading: sessionLoading } = useCurrentUser();

  const me = useQuery({
    queryKey: ['me-view'],
    enabled: !!user?.person_id,
    queryFn: () => api<MeView>('/api/me'),
  });

  if (sessionLoading) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  // No session (expired or signed out): branded login page, not a stub.
  if (!user) return <Navigate to="/login" replace />;
  if (!user.person_id) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        This page is for attendees. <Link to="/" className="underline">Home</Link>.
      </main>
    );
  }
  if (me.isLoading) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  if (me.error) {
    const status = me.error instanceof ApiError ? me.error.status : 0;
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <EmptyState
          icon={<AlertCircle className="h-6 w-6" />}
          title="Couldn't load this task"
          description={`The server returned ${status}.`}
        />
      </main>
    );
  }

  const task = me.data?.tasks.find((t) => t.task_id === taskId);
  const kind = task?.onboarding_kind;
  const supported = kind === 'todo' || kind === 'upload' || kind === 'check';

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-2xl space-y-5 px-4 py-8 sm:px-6">
        <Link
          to="/me"
          className="inline-flex items-center gap-1 text-sm font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to your onboarding
        </Link>

        {!task || !supported ? (
          <EmptyState
            icon={<AlertCircle className="h-6 w-6" />}
            title="Task not found"
            description="This task isn't assigned to you, or it lives on another page."
          />
        ) : (
          <>
            <Hero
              eyebrow={<span>Onboarding</span>}
              title={task.title}
              description={
                kind === 'check' ? (
                  'Follow the steps below; we track each stage for you.'
                ) : kind === 'upload' ? (
                  'Attach the file(s) below and submit them for HR to review.'
                ) : task.description ? (
                  <LinkifiedText text={task.description} />
                ) : (
                  'Mark this done once you have completed it.'
                )
              }
            />
            <Card>
              {kind === 'check' ? <CheckTaskRow task={task} /> : null}
              {kind === 'todo' ? <TodoBody task={task} /> : null}
              {kind === 'upload' ? <UploadBody task={task} /> : null}
            </Card>
          </>
        )}
      </div>
    </main>
  );
}

/** A simple to-do: nothing to fill in, the inductee just marks it done. */
function TodoBody({ task }: { task: OnboardingTask }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const locked = task.status === 'complete';

  const markDone = useMutation({
    mutationFn: () =>
      api<{ event: unknown }>(`/api/me/tasks/${task.task_id}/submit`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      toast.success('Marked done');
      qc.invalidateQueries({ queryKey: ['me-view'] });
      navigate('/me');
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not update'),
  });

  return (
    <CardSection className="space-y-3">
      <div>{statusPill(task.status)}</div>
      {task.status === 'changes_requested' && task.rejection_reason ? (
        <p className="text-sm text-warning-soft-fg">{task.rejection_reason}</p>
      ) : null}
      {!locked ? (
        <Button
          size="md"
          variant="success"
          iconLeft={<CheckCircle2 className="h-4 w-4" />}
          loading={markDone.isPending}
          onClick={() => markDone.mutate()}
        >
          Mark done
        </Button>
      ) : (
        <p className="text-sm text-fg-muted">All done; nothing more to do here.</p>
      )}
    </CardSection>
  );
}

/** An upload task: attach files, submit for HR approval. */
function UploadBody({ task }: { task: OnboardingTask }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [files, setFiles] = useState<PendingFile[]>([]);
  const locked = task.status === 'complete';

  const submit = useMutation({
    mutationFn: () =>
      api<{ ok: true }>(`/api/me/onboarding/uploads/${task.task_id}/submit`, {
        method: 'POST',
        body: JSON.stringify({
          files: files.map((f) => ({ filename: f.filename, base64: f.base64 })),
        }),
      }),
    onSuccess: () => {
      toast.success('Uploaded');
      qc.invalidateQueries({ queryKey: ['me-view'] });
      navigate('/me');
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not upload'),
  });

  // Synthetic field def so we can reuse the shared uploader (any safe type, many files).
  const field: FormFieldDef = {
    key: 'upload',
    type: 'file_upload',
    label: 'Files',
    required: true,
    multiple: true,
  };

  return (
    <CardSection className="space-y-3">
      <div>{statusPill(task.status)}</div>
      {task.description ? (
        <p className="text-sm text-fg-muted">
          <LinkifiedText text={task.description} />
        </p>
      ) : null}
      {task.status === 'changes_requested' && task.rejection_reason ? (
        <p className="text-sm text-warning-soft-fg">{task.rejection_reason}</p>
      ) : null}
      {locked ? (
        <p className="text-sm text-fg-muted">All done; nothing more to do here.</p>
      ) : (
        <>
          <FileUploadField field={field} files={files} onChange={setFiles} />
          <Button
            size="md"
            variant="primary"
            loading={submit.isPending}
            disabled={files.length === 0}
            onClick={() => submit.mutate()}
          >
            {task.status === 'pending' ? 'Submit' : 'Submit again'}
          </Button>
        </>
      )}
    </CardSection>
  );
}
